<script module lang="ts">
  const defaultTypeByClaimProperty = {
    'wdt:P135': 'movement',
    'wdt:P136': 'genre',
    'wdt:P7937': 'form',
  } as const
</script>
<script lang="ts">
  import { debounce } from 'lodash-es'
  import { setContext } from 'svelte'
  import { isNonEmptyPlainObject } from '#app/lib/boolean_tests'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { loadInternalLink } from '#app/lib/utils'
  import ActorFollowersSection from '#entities/components/layouts/actor_followers_section.svelte'
  import SubEntitiesBrowser from '#entities/components/layouts/sub_entities_browser.svelte'
  import Summary from '#entities/components/layouts/summary.svelte'
  import { getSubentitiesTypes } from '#entities/components/lib/claim_layout_helpers'
  import { getSubEntitiesSections, type SubEntitiesBrowserSectionData } from '#entities/components/lib/entities'
  import { getRelativeEntitiesListLabel, getRelativeEntitiesProperties } from '#entities/components/lib/relative_entities_helpers'
  import { runEntityNavigate } from '#entities/lib/document_metadata'
  import type { SerializedEntity } from '#entities/lib/entities'
  import { isEntityType } from '#entities/lib/types/entities_types'
  import Spinner from '#general/components/spinner.svelte'
  import type { PropertyUri } from '#server/types/entity'
  import EntityImage from '../entity_image.svelte'
  import BaseLayout from './base_layout.svelte'
  import RelativeEntitiesList from './relative_entities_list.svelte'

  let {
    entity = $bindable(),
    property = $bindable(),
  }: {
    entity: SerializedEntity
    property: PropertyUri
  } = $props()

  let flash: FlashState = $state()

  const { uri } = entity
  // Infer a type from a property
  entity.type ??= defaultTypeByClaimProperty[property]
  const { label, type } = entity

  runEntityNavigate(entity, { uriPrefix: `${property}-` })

  setContext('layout-context', type)
  setContext('search-filter-claim', `${property}=${uri}`)

  const searchTypes = getSubentitiesTypes(property)
  if (searchTypes.length > 0) setContext('search-filter-types', searchTypes)

  const typeLabel = defaultTypeByClaimProperty[property] || 'subject'

  let sections: SubEntitiesBrowserSectionData[] = $state()
  let waitingForReverseEntities = $state()
  function getSections () {
    waitingForReverseEntities = getSubEntitiesSections({ entity, property })
      .then(res => sections = res)
      .catch(err => flash = err)
  }
  const lazyGetSections = debounce(getSections, 100)
  $effect(() => {
    if (entity) lazyGetSections()
  })
</script>

<BaseLayout
  {entity}
  bind:flash
  {typeLabel}
  showEntityEditButtons={false}
>
  {#snippet entityLayoutMain()}
    <div class="entity-layout">
      <div class="title-row">
        <a
          href={`/entity/${uri}`}
          onclick={loadInternalLink}
        >
          <h2>
            {label}
          </h2>
        </a>
      </div>
      <div class="top-section">
        {#if !isEntityType(type)}
          {#if isNonEmptyPlainObject(entity.image)}
            <EntityImage
              {entity}
              size={192}
            />
          {/if}
          <Summary {entity} />
        {/if}
      </div>
      <div class="relatives-browser">
        {#await waitingForReverseEntities}
          <Spinner center={true} />
        {:then}
          <SubEntitiesBrowser {sections} />
        {/await}
      </div>
      <div class="relatives-lists">
        {#each getRelativeEntitiesProperties(type, property) as relativeProperty (relativeProperty)}
          <RelativeEntitiesList
            {entity}
            property={relativeProperty}
            label={getRelativeEntitiesListLabel({ property: relativeProperty, entity })}
          />
        {/each}
      </div>
      <ActorFollowersSection uri={entity.uri} />
    </div>
  {/snippet}
</BaseLayout>

<style lang="scss">
  @import "general/scss/utils";
  .entity-layout{
    align-self: stretch;
    @include display-flex(column, stretch);
  }
  a:hover{
    text-decoration: underline;
  }
  .top-section{
    @include display-flex(row, center);
    :global(.entity-image){
      flex: 1 0 auto;
      margin-inline-end: 1em;
    }
  }
  .title-row{
    @include display-flex(row, center);
  }
  .relatives-browser{
    margin-block-start: 1em;
  }
  /* Smaller screens */
  @media screen and (width < $smaller-screen){
    .top-section{
      display: block;
      :global(.entity-image){
        margin-block-end: 1em;
      }
    }
  }
</style>
